import { Link, useOutletContext } from "@remix-run/react"
import { ShoppingCart } from "lucide-react"
import { useMemo, useState } from "react"
import toast from "react-hot-toast"
import { CardProduct, OutletContext } from "~/types"
import { formatCurrency } from "~/utils/utils"
import ImportedByNomin from "../badge/ImportedByNomin"
import { Button } from "../ui/button"
import MiniQuantitySelector from "./MiniQuantitySelector"
import ProductWishlist from "./ProductWishlistsDialog"

import { ClientOnly } from "remix-utils/client-only"
import LottieWrapper from "~/components/LottieWrapper"
import giftProd from "~/components/products/giftProd.json"
import { trackEvent } from "~/firebase.client"
import ProductCardTimer from "../product-details/ProductCardTimer"
import Gift from "../badge/Gift"

export default function NewArrivalSmallProductCard({
  item,
}: {
  item: CardProduct
}) {
  const {
    activeOrder,
    activeOrderFetcher,
    adjustOrderLine,
    addItemToCart,
    removeItem,
  } = useOutletContext<OutletContext>()

  const [showQuantitySelector, setShowQuantitySelector] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isFavorite, setIsFavorite] = useState(false)

  const { isSignedIn } = useOutletContext<any>()
  const itemType = item.itemType
  const isDiscounted = item.discountPercent != 0

  const findProductInCart = (variantId: string) => {
    const line = activeOrder?.lines?.find(
      (line) => line.productVariant.id === variantId
    )
    return line || undefined
  }

  const renderAddOrAdjustProductToCart = (item: CardProduct) => {
    const cartLine = findProductInCart(item.defaultVariantId)

    // 🔹 Always show "Add to Cart" on mobile
    return (
      <>
        {/* Mobile: only button */}
        <div className="block sm:hidden">
          <Button
            type="button"
            onClick={() => {
              if (item.inStock) {
                trackEvent("add_to_cart", {
                  item_id: item.slug,
                  item_name: item.name,
                  item_variant: item.defaultVariantId,
                  quantity: 1,
                  price: Number(item.computedPrice ?? 0),
                  currency: "MNT",
                  value: Number(item.computedPrice ?? 0),
                })
                addItemToCart(item.defaultVariantId, 1)
                toast.success("Барааг нэмлээ", {
                  duration: 1000,
                  icon: "🛍️",
                })
              } else {
                toast.error("Үлдэгдэлгүй", { duration: 1000 })
              }
            }}
            disabled={activeOrderFetcher.state !== "idle"}
            className="bg-primary hover:bg-primary/90 text-white rounded-full p-2.5 h-auto add-to-cart "
          >
            <ShoppingCart className="w-[18px] h-[18px]" />
          </Button>
        </div>

        {/* Desktop: existing logic */}
        <div className="hidden sm:block">
          {cartLine ? (
            <MiniQuantitySelector
              qty={cartLine.quantity}
              min={0}
              onAdjust={(qty: number) => {
                if (qty === 0) removeItem?.(cartLine.id)
                else adjustOrderLine?.(cartLine.id, qty)
              }}
            />
          ) : showQuantitySelector ? (
            <MiniQuantitySelector
              qty={1}
              onAdjust={(qty: number) =>
                addItemToCart(item.defaultVariantId, qty)
              }
            />
          ) : (
            <Button
              type="button"
              onClick={() => {
                if (item.inStock) {
                  trackEvent("add_to_cart", {
                    item_id: item.slug,
                    item_name: item.name,
                    item_variant: item.defaultVariantId,
                    quantity: 1,
                    price: Number(item.computedPrice ?? 0),
                    currency: "MNT",
                    value: Number(item.computedPrice ?? 0),
                  })
                  addItemToCart(item.defaultVariantId, 1)
                  toast.success("Барааг нэмлээ", {
                    duration: 1000,
                    icon: "🛍️",
                  })
                } else {
                  toast.error("Үлдэгдэлгүй", { duration: 1000 })
                }
              }}
              disabled={activeOrderFetcher.state !== "idle"}
              className="bg-primary hover:bg-primary/90 text-white rounded-full p-2.5 h-auto add-to-cart "
            >
              <ShoppingCart className="w-[18px] h-[18px]" />
            </Button>
          )}
        </div>
      </>
    )
  }

  const renderWishlistButton = () => {
    if (!isSignedIn) {
      return null
    }

    return <ProductWishlist productVariantId={item.defaultVariantId} />
  }
  const hasGift = item.giftProducts
  const animationData = useMemo(() => giftProd, [])
  const isAlcoholic = useMemo(() => item.productType === "alcoholic", [])

  return (
    <div className="group flex overflow-hidden border border-[#F5F3F6] rounded-2xl md:rounded-[30px] h-fit relative flex-col justify-between transition-all duration-300 ease-in-out w-full hover:scale-[1.02] shadow-[0px_0px_1px_0px_rgba(0,0,0,0.04),0px_0px_6px_0px_rgba(0,0,0,0.04),0px_0px_20px_0px_rgba(0,0,0,0.04)] hover:shadow-[0px_10px_20px_0px_rgba(77,104,239,0.18)] bg-white/10">
      <div className="flex-grow relative">
        <Link
          to={`/p/${item.slug}`}
          preventScrollReset={false}
          data-product-id={item.id}
          data-product-name={item.name}
          data-product-price={item.priceWithTax}
        >
          <div className="relative group rounded-lg h-fit p-2 md:p-4">
            <div className="relative">
              <div className="aspect-square mx-auto max-w-[230px]">
                <img
                  className={`transition-all duration-300 object-contain w-full h-full ${
                    isAlcoholic ? "blur-sm group-hover:blur-none" : ""
                  }`}
                  alt="product"
                  src={
                    item.assets?.[currentImageIndex] !== undefined
                      ? `https://assets.nomin.mn/${item.assets?.[currentImageIndex]}?imageMogr2/format/webp/interlace/0/quality/90/thumbnail/230x`
                      : "https://assets.nomin.mn/web/images/nomin_placeholder.png"
                  }
                />
              </div>
            </div>

            <div className="absolute right-2 top-2 flex flex-col items-end">
              {isDiscounted && (
                <div className="text-white rounded-xl w-fit px-2 text-sm font-medium py-1 bg-red-500 border border-red-500">
                  -{item?.discountPercent}%
                </div>
              )}
              {/* {item.itemType === "N" && <ImportedByNomin />} */}
            </div>
            <div className="absolute left-2 top-2 flex flex-col items-start">
              {item.itemType === "N" && <ImportedByNomin />}
              {hasGift ? <Gift /> : null}
            </div>

            {item.assets?.length > 1 && (
              <div className="absolute inset-0">
                <div
                  className={`h-full grid ${
                    item.assets.length === 2 ? "grid-cols-2" : "grid-cols-3"
                  }`}
                >
                  {item.assets
                    .slice(0, item.assets.length === 2 ? 2 : 3)
                    .map((_, index) => (
                      <div
                        key={index}
                        className="relative cursor-pointer group"
                        onMouseEnter={() => setCurrentImageIndex(index)}
                        onMouseLeave={() => setCurrentImageIndex(0)}
                      >
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center opacity-0 mx-1 group-hover:opacity-100 transition-opacity">
                          <div
                            className={`w-full h-0.5 rounded-full transition-colors ${
                              index === currentImageIndex
                                ? "bg-[#3352ec]"
                                : "bg-gray-300"
                            }`}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </Link>

        <div className="flex justify-between items-end absolute bottom-1.5 left-0 w-full px-2">
          <div className="gap-2 flex items-center"></div>
          <div className="">
            <div className="flex items-end flex-col gap-2">
              <div>{renderWishlistButton()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
